import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import useLenis from "../lib/useLenis";

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/capabilities", label: "Capabilities" },
  { to: "/contact", label: "Contact Us" },
];

function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-white/95 backdrop-blur-sm border-b border-[var(--line)]" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 flex items-center justify-between h-16 md:h-20">
        <Link to="/" data-testid="nav-logo" className="group flex items-center gap-3">
          <span className="block w-6 h-6 border border-[var(--navy)] relative">
            <span className="absolute inset-[3px] bg-[var(--navy)]" />
          </span>
          <span className="font-serif text-[18px] md:text-[20px] tracking-[0.14em] uppercase text-[var(--navy)]">
            Aurelius <span className="text-[var(--gold-soft)]">Dynamics</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}-link`}
              className={({ isActive }) =>
                `relative text-[13px] uppercase tracking-[0.18em] font-medium transition-colors duration-300 ${
                  isActive ? "text-[var(--navy)]" : "text-[var(--steel)] hover:text-[var(--navy)]"
                }`
              }
            >
              {({ isActive }) => (
                <span className="flex items-center gap-2">
                  {isActive && <span className="inline-block w-1 h-1 bg-[var(--gold)]" />}
                  {l.label}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
          className="md:hidden text-[var(--navy)]"
          onClick={() => setOpen(!open)}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.76, 0, 0.24, 1] }}
            className="md:hidden overflow-hidden bg-white border-t border-[var(--line)]"
          >
            <div className="flex flex-col px-6 py-6 gap-5">
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                  className="text-[var(--navy)] text-2xl font-serif tracking-tight"
                >
                  {l.label}
                </NavLink>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer data-testid="site-footer" className="bg-[var(--navy-deep)] text-white/80 mt-24">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 border-b border-white/10 pb-16">
          <div className="md:col-span-5">
            <Link to="/" className="flex items-center gap-3">
              <span className="block w-7 h-7 border border-[var(--gold)] relative">
                <span className="absolute inset-[3px] bg-[var(--gold)]" />
              </span>
              <span className="font-serif text-xl tracking-[0.14em] uppercase text-white">
                Aurelius <span className="text-[var(--gold)]">Dynamics</span>
              </span>
            </Link>
            <p className="mt-6 text-white/60 text-sm leading-relaxed max-w-md">
              Engineering advanced solutions in nuclear technology, space research, and strategic minerals for governments, research institutions, and commercial partners worldwide.
            </p>
            <p className="mt-6 eyebrow text-white/40">
              1200 Innovation Parkway · Houston, Texas 77002 · USA
            </p>
          </div>

          <div className="md:col-span-3">
            <p className="eyebrow-gold mb-5">Explore</p>
            <ul className="space-y-3 text-sm">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="link-underline text-white/80 hover:text-white">{l.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <p className="eyebrow-gold mb-5">Legal</p>
            <ul className="space-y-3 text-sm">
              <li><a href="#" className="link-underline text-white/80" data-testid="footer-privacy-link">Privacy Policy</a></li>
              <li><a href="#" className="link-underline text-white/80" data-testid="footer-terms-link">Terms &amp; Conditions</a></li>
              <li><a href="#" className="link-underline text-white/80" data-testid="footer-cookie-link">Cookie Policy</a></li>
            </ul>
            <p className="mt-8 eyebrow-gold mb-4">Global Enquiries</p>
            <a href="mailto:contact@aureliusdynamics.com" className="text-white text-sm link-underline">contact@aureliusdynamics.com</a>
            <p className="text-white/60 text-sm mt-2">+1 (713) 555-0186</p>
          </div>
        </div>

        <div className="flex flex-col md:flex-row justify-between gap-4 pt-8 text-xs text-white/50 uppercase tracking-[0.18em]">
          <p>© {year} Aurelius Dynamics. All Rights Reserved.</p>
          <p className="font-mono">est. 1968 · Houston · TX</p>
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  useLenis();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
